import React, { memo, useEffect, useMemo, useState } from "react";
import { PolicyFullObject } from "../../types/insurance.ts";
import moment from "moment";
import DatePicker from "./DatePicker/DatePicker.tsx";
import SelectGroup from "./SelectGroup/SelectGroup.tsx";
import SectionHeader from "../SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

interface SumsAndDatesFormProps {
  policyData: PolicyFullObject;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setPolicyData: React.Dispatch<React.SetStateAction<PolicyFullObject>>;
  addRowsBasedOnInstallments: (value: number) => void;
}

interface LocalSumsState {
  insuredSum: string;
  policySum: string;
  gfSum: string;
  agentCommissionPercentage: string;
  policyAdditionalData: string;
}

const DATE_FORMAT = "DD/MM/YYYY";
const MAX_MONTHS_DIFF = 9;

const SumsAndDatesFormComponent: React.FC<SumsAndDatesFormProps> = ({
  policyData,
  handleChange,
  setPolicyData,
  addRowsBasedOnInstallments,
}) => {
  const [local, setLocal] = useState<LocalSumsState>({
    insuredSum: policyData.insuredSum || "",
    policySum: String(policyData.policySum ?? ""),
    gfSum: String(policyData.gfSum ?? ""),
    agentCommissionPercentage: policyData.agentCommissionPercentage || "0",
    policyAdditionalData: policyData.policyAdditionalData || "",
  });

  useEffect(() => {
    setLocal({
      insuredSum: policyData.insuredSum || "",
      policySum: String(policyData.policySum ?? ""),
      gfSum: String(policyData.gfSum ?? ""),
      agentCommissionPercentage: policyData.agentCommissionPercentage || "0",
      policyAdditionalData: policyData.policyAdditionalData || "",
    });
  }, [policyData.policyId, policyData.policyNumber]);

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocal((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field: keyof LocalSumsState) => () => {
    handleChange({
      target: { name: field, value: local[field] },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const contractDateValue =
    policyData.contractDate || moment().format(DATE_FORMAT);
  const startDateValue =
    policyData.startDate || moment().add(1, "days").format(DATE_FORMAT);

  const dateGapError = useMemo(() => {
    const contract = moment(contractDateValue, DATE_FORMAT, true);
    const start = moment(startDateValue, DATE_FORMAT, true);

    if (!contract.isValid() || !start.isValid()) return false;

    const monthsDiff = Math.abs(start.diff(contract, "months", true));
    return monthsDiff > MAX_MONTHS_DIFF;
  }, [contractDateValue, startDateValue]);

  return (
    <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
      <SectionHeader title="Суми и дати" />

      <div className="p-3 pt-1">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <div>
            <label className={labelClass}>Застрахователна сума</label>
            <input
              name="insuredSum"
              type="text"
              onChange={handleLocalChange}
              onBlur={handleBlur("insuredSum")}
              value={local.insuredSum}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Застрахователна премия</label>
            <input
              name="policySum"
              type="text"
              onChange={handleLocalChange}
              onBlur={handleBlur("policySum")}
              value={local.policySum}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>ГФ Сума</label>
            <input
              name="gfSum"
              type="text"
              onChange={handleLocalChange}
              onBlur={handleBlur("gfSum")}
              value={local.gfSum}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Комис. Агент</label>
            <input
              name="agentCommissionPercentage"
              onChange={handleLocalChange}
              onBlur={handleBlur("agentCommissionPercentage")}
              value={local.agentCommissionPercentage}
              className={inputClass}
            />
          </div>

          {/* Dates go directly to parent — DatePicker doesn't benefit from local state */}
          <div>
            <DatePicker
              label="Дата на сключване"
              onChange={handleChange}
              value={contractDateValue}
              name="contractDate"
              classNameAdditional={
                dateGapError ? "border-danger text-danger" : ""
              }
            />
            {dateGapError && (
              <p className="text-danger mt-1 text-xs">
                Разликата между датата на сключване и началната дата е повече от{" "}
                {MAX_MONTHS_DIFF} месеца.
              </p>
            )}
          </div>
          <div>
            <DatePicker
              label="Начална дата"
              onChange={handleChange}
              value={startDateValue}
              name="startDate"
              classNameAdditional={
                dateGapError ? "border-danger text-danger" : ""
              }
            />
            {dateGapError && (
              <p className="text-danger mt-1 text-xs">
                Разликата между датата на сключване и началната дата е повече от{" "}
                {MAX_MONTHS_DIFF} месеца.
              </p>
            )}
          </div>
          <div>
            <DatePicker
              label="Крайна дата"
              onChange={handleChange}
              value={
                policyData.endDate ||
                moment().add(1, "year").subtract(1, "days").format(DATE_FORMAT)
              }
              name="endDate"
            />
          </div>
          <div>
            <DatePicker
              label="Дата на отчитане"
              onChange={handleChange}
              value={policyData.reportDate || moment().format(DATE_FORMAT)}
              name="reportDate"
            />
          </div>

          <div>
            <label className={labelClass}>Нулев комисион</label>
            <SelectGroup
              onChange={(selectedValue) =>
                setPolicyData((prev) => ({
                  ...prev,
                  zeroCommission: selectedValue,
                }))
              }
              classNameAdditional="w-full"
              options={{ 0: "Не", 1: "Да" }}
              placeholder="Не"
              value={policyData.zeroCommission}
            />
          </div>
          <div>
            <label className={labelClass}>Статус на полицата</label>
            <SelectGroup
              onChange={(selectedValue) =>
                setPolicyData((prev) => ({
                  ...prev,
                  policyStatus: selectedValue,
                }))
              }
              classNameAdditional="w-full"
              options={{
                1: "Активна",
                2: "Приключена",
                3: "Прекратена",
                4: "Анулирана",
              }}
              placeholder="Активна"
              value={policyData.policyStatus}
            />
          </div>
          <div>
            <label className={labelClass}>Брой вноски</label>
            <SelectGroup
              shouldSort={false}
              onChange={(selectedValue: number) => {
                setPolicyData((prev) => ({
                  ...prev,
                  installmentsCount: selectedValue as number,
                }));
                addRowsBasedOnInstallments(selectedValue);
              }}
              classNameAdditional="w-full"
              options={Array.from({ length: 120 }, (_, i) => i + 1).reduce(
                (acc, num) => {
                  acc[num] = num;
                  return acc;
                },
                { 0: "Моля, изберете" }
              )}
              value={policyData.installmentsCount}
            />
          </div>
          <div>
            <label className={labelClass}>Допълнителна информация</label>
            <input
              name="policyAdditionalData"
              onChange={handleLocalChange}
              onBlur={handleBlur("policyAdditionalData")}
              value={local.policyAdditionalData}
              type="text"
              className={inputClass}
            />
          </div>
        </div>
      </div>
    </fieldset>
  );
};

SumsAndDatesFormComponent.displayName = "SumsAndDatesForm";
const SumsAndDatesForm = memo(SumsAndDatesFormComponent);
export default SumsAndDatesForm;
